import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'

import { Direction, ThresholdUnit, TierPoints, TierThresholds } from '../../data/risk-scorecard-config.interface'
import { UNIT_SUFFIX, fromDisplay, toDisplay } from '../../data/risk-scorecard-config.labels'

/**
 * Los tres umbrales de una métrica, su dirección y lo que vale cada tramo (plan v4.2 §7.2 y v4.3 D-51). El tramo alto
 * vale siempre 20 (el peso completo); el medio y el bajo se editan y tienen que ir de mayor a menor. No reordena nada:
 * si algo no va en orden, avisa y la API rechaza al guardar. Un porcentaje se captura como porcentaje (10) y se guarda
 * como fracción (0.10). No es `config-tier-editor` de ofertas, que es una lista variable.
 */
@Component({
  selector: 'app-scorecard-tier-editor',
  templateUrl: './scorecard-tier-editor.component.html',
  styleUrls: ['./scorecard-tier-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScorecardTierEditorComponent {
  @Input() tiers: TierThresholds = { t20: 0, t15: 0, t10: 0 }
  @Input() direction: Direction = 'higher_better'
  @Input() unit: ThresholdUnit = 'count'
  @Input() readonly = false
  /** Lo que vale cada tramo; sin él, 20/15/10/0. `null` para los tramos extra de una regla, que no puntúan solos. */
  @Input() tierPoints: TierPoints | null | undefined = undefined
  @Input() showPoints = true
  /** Nombre del renglón para los rótulos accesibles de los campos. */
  @Input() label = ''
  @Output() tiersChange = new EventEmitter<TierThresholds>()
  @Output() directionChange = new EventEmitter<Direction>()
  @Output() tierPointsChange = new EventEmitter<TierPoints>()

  readonly keys: (keyof TierThresholds)[] = ['t20', 't15', 't10']
  static readonly DEFAULT_POINTS: TierPoints = { t20: 20, t15: 15, t10: 10, t0: 0 }

  get points(): TierPoints {
    return this.tierPoints ?? ScorecardTierEditorComponent.DEFAULT_POINTS
  }

  //* El alto vale 20 y ninguno sube de tramo en tramo, como valida la API.
  get pointsOrdered(): boolean {
    const { t20, t15, t10, t0 } = this.points
    if ([t15, t10].some((value) => typeof value !== 'number' || !Number.isFinite(value))) return false
    return t20 === 20 && t20 >= t15 && t15 >= t10 && t10 >= t0 && t0 >= 0
  }

  get customPoints(): boolean {
    const { t15, t10, t0 } = this.points
    return t15 !== 15 || t10 !== 10 || t0 !== 0
  }

  setPoints(key: 't15' | 't10', raw: string | number | null): void {
    const value = raw === '' || raw === null ? null : Number(raw)
    this.tierPointsChange.emit({ ...this.points, [key]: value as number })
  }

  resetPoints(): void {
    this.tierPointsChange.emit({ ...ScorecardTierEditorComponent.DEFAULT_POINTS })
  }

  get suffix(): string {
    return UNIT_SUFFIX[this.unit]
  }

  //* Orden NO estricto, igual que la API: dos umbrales iguales son válidos.
  get ordered(): boolean {
    const { t20, t15, t10 } = this.tiers ?? ({} as TierThresholds)
    if ([t20, t15, t10].some((value) => typeof value !== 'number' || !Number.isFinite(value))) return false
    return this.direction === 'higher_better' ? t20 >= t15 && t15 >= t10 : t20 <= t15 && t15 <= t10
  }

  //* Rotula cada umbral con lo que vale su tramo: con puntos propios (C3a 20/10/5) el tramo medio ya no es «15 pts».
  get orderHint(): string {
    const higher = this.direction === 'higher_better'
    const tiers = this.keys.map((key) => `${this.points[key]} pts`).join(higher ? ' ≥ ' : ' ≤ ')
    return `${higher ? 'Más es mejor' : 'Menos es mejor'}: ${tiers}`
  }

  shown(key: keyof TierThresholds): number | null {
    return toDisplay(this.tiers?.[key], this.unit)
  }

  setTier(key: keyof TierThresholds, raw: string | number | null): void {
    const parsed = raw === '' || raw === null ? null : Number(raw)
    const value = fromDisplay(parsed, this.unit)
    this.tiersChange.emit({ ...this.tiers, [key]: value })
  }

  setDirection(direction: Direction): void {
    if (direction !== this.direction) this.directionChange.emit(direction)
  }
}
